import { Injectable } from '@angular/core';
import {
  Purchases,
  LogLevel,
  CustomerInfo,
  Package,
  PurchaseResult,
} from '@revenuecat/purchases-js';
import { BehaviorSubject } from 'rxjs';

import { environment } from 'src/environments/environment';
import { logger } from '#lib/logger';
import { AuthService } from '#services/auth/auth.service';
import { IPurchaseService } from './purchase.interface';
import { ENTITLEMENT_NAME, Product } from '#models/subscription/product.model';
import { IntermediaryService } from '#services/intermediary/intermediary.service';
import { B2B_OFFERING } from '#models/subscription/user-redemption.model';
import { RedemptionService } from '#services/redemption/redemption.service';

@Injectable({
  providedIn: 'root',
})
export class PurchaseWebService implements IPurchaseService {
  products$ = new BehaviorSubject<Product[]>([]);
  userHasSubscribedBefore$ = new BehaviorSubject<boolean>(false);

  private _Purchases: Purchases | null = null;

  get Purchases(): Purchases | null {
    return this._Purchases;
  }

  constructor(
    private authService: AuthService,
    private intermediaryHandler: IntermediaryService,
    private redemptionService: RedemptionService
  ) {}

  async init() {
    const appUserId = this.authService.getPublicUserId(true);

    // Check if the Purchases instance is already configured. This can happen if multiple users use the same browser.
    // Fringe case only, but we handle it gracefully.
    if (this.isConfigured()) {
      logger.log('', 'PurchaseService.init -> Already configured');

      await this.Purchases!.changeUser(appUserId);

      logger.log({ appUserId }, 'PurchaseService.init -> User changed');
      return;
    }

    logger.log({ appUserId }, 'PurchaseService.init -> appUserId');

    Purchases.setLogLevel(LogLevel.Debug);

    this._Purchases = Purchases.configure({
      apiKey: environment.revenueCatApiKey.web,
      appUserId,
    });

    logger.log('web', 'PurchaseService.init -> Web RevenueCat configured');
  }

  isSandbox(): boolean {
    return this.Purchases?.isSandbox() ?? false;
  }

  async logInCustomerInfo(_userId: string): Promise<void> {
    logger.log('', 'PurchaseWebService.logInCustomerInfo -> No-op on web platform');
  }

  async logOutCustomerInfo(): Promise<void> {
    logger.log('', 'PurchaseService.logOutCustomerInfo -> Logging out user');

    this.products$.next([]);
    this.userHasSubscribedBefore$.next(false);

    if (!this.Purchases) {
      return;
    }

    this.Purchases!.close();
  }

  isConfigured(): boolean {
    return this.Purchases !== null;
  }

  async getCustomerInfo(): Promise<CustomerInfo> {
    if (!this.isConfigured()) {
      this.init();
    }

    const customerInfo = await this.Purchases!.getCustomerInfo();

    logger.log(customerInfo, 'PurchaseService.getCustomerInfo -> Customer Info retrieved');
    return customerInfo!;
  }

  async getOfferings(): Promise<Package[]> {
    if (!this.isConfigured()) {
      this.init();
    }

    const offerings = await this.Purchases!.getOfferings();

    // Check if the user has redeemed an offer code, if so, we show them the B2B offering.
    const offerCodeRedeemed = await this.redemptionService.verifyUserRedemption(true);

    // Bail early if no offerings are available.
    if (!offerings) {
      return [];
    }

    // Bail early if no current offerings are available for standard offers.
    if (!offerCodeRedeemed && offerings.current === null) {
      return [];
    }

    const availablePackages = !offerCodeRedeemed
      ? (offerings.current?.availablePackages ?? [])
      : offerings.all[B2B_OFFERING]?.availablePackages;

    logger.log(
      availablePackages ?? [],
      'PurchaseService.getOfferings -> Offering packages retrieved'
    );

    const products = availablePackages.map((pkg: Package) => {
      return this.mapPurchasePackagetoProduct(pkg);
    });

    this.products$.next(products);

    logger.log(this.products$.value, 'PurchaseService.getOfferings -> Available products');

    return offerings.current?.availablePackages ?? [];
  }

  /**
   * Maps a RevenueCat Package to an internal Product model. This helps to standardize the product data
   * across the application.
   *
   * @param purchasePackage The RevenueCat Package to map.
   * @returns The mapped Product model.
   */
  private mapPurchasePackagetoProduct(pkg: Package): Product {
    const subscriptionOption = pkg.webBillingProduct.defaultSubscriptionOption;
    const trial = subscriptionOption?.trial
      ? {
          periodDuration: subscriptionOption.trial.periodDuration ?? '',
          period: {
            number: subscriptionOption.trial.period?.number ?? 0,
            unit: subscriptionOption.trial.period?.unit ?? 'month',
          },
          cycleCount: subscriptionOption.trial.cycleCount ?? 1,
        }
      : null;
    const subscriptionPeriod = subscriptionOption
      ? {
          periodDuration: subscriptionOption.base.periodDuration ?? '',
          period: {
            number: subscriptionOption.base.period?.number ?? 0,
            unit: subscriptionOption.base.period?.unit ?? 'month',
          },
          cycleCount: subscriptionOption.base.cycleCount ?? 1,
          pricePerWeek: subscriptionOption.base.pricePerWeek ?? null,
          pricePerMonth: subscriptionOption.base.pricePerMonth ?? null,
          pricePerYear: subscriptionOption.base.pricePerYear ?? null,
        }
      : null;

    const mappedPackage: Product = {
      identifier: pkg.identifier,
      title: pkg.webBillingProduct.title,
      description: pkg.webBillingProduct.description ?? '',
      currentPrice: {
        amount: pkg.webBillingProduct.currentPrice.amount,
        amountMicros: pkg.webBillingProduct.currentPrice.amountMicros,
        currency: pkg.webBillingProduct.currentPrice.currency,
        formattedPrice: pkg.webBillingProduct.currentPrice.formattedPrice,
      },
      subscriptionPeriod,
      trial,
      rcPackage: pkg,
    };

    return mappedPackage;
  }

  async checkIfUserIsSubscribed(entitlements?: 'active' | 'all'): Promise<boolean> {
    const customerInfo = await this.getCustomerInfo();
    const hasActiveSubscription =
      ENTITLEMENT_NAME in customerInfo.entitlements[entitlements || 'active'];

    if (entitlements && entitlements === 'all') {
      this.userHasSubscribedBefore$.next(hasActiveSubscription);
    }

    logger.log(
      hasActiveSubscription,
      `PurchaseService.checkIfUserIsSubscribed -> Result for entitlements: ${entitlements || 'active'}`
    );

    return hasActiveSubscription;
  }

  async purchaseProduct(
    rcPackage: Package,
    nextFn?: (purchaseResult: PurchaseResult | null) => void
  ): Promise<void> {
    if (!this.isConfigured()) {
      this.init();
    }

    this.intermediaryHandler.intermediary(
      {
        nextFn,
      },
      async () => {
        const purchaseResult = await this.Purchases!.purchase({ rcPackage });

        logger.log(purchaseResult, 'PurchaseService.purchaseProduct -> Purchase successful');

        // Call `checkIfUserIsSubscribed` to update the BehaviorSubject.
        const hasSubscribedBefore = await this.checkIfUserIsSubscribed('all');

        logger.log(hasSubscribedBefore, 'PurchaseService.purchaseProduct -> User has subscribed');

        return purchaseResult;
      }
    );
  }

  resetPurchases() {
    this.products$.next([]);
    this.userHasSubscribedBefore$.next(false);
    logger.log('complete', 'PurchaseService.resetPurchases');
  }
}
